'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { T } from '@/lib/tokens';
import { Orb } from '@/components/interview/Orb';
import { useRealtimeSession } from '@/hooks/useRealtimeSession';

function fmtTime(s: number) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── Small shared UI atoms ────────────────────────────────────────────────────

function IconBtn({
  onClick,
  label,
  active = false,
  danger = false,
  disabled = false,
  children,
}: {
  onClick: () => void;
  label: string;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={label}
      aria-label={label}
      style={{
        width: 56, height: 56, borderRadius: '50%',
        border: `1px solid ${T.creamBorder}`,
        background: disabled ? T.creamMid : active ? T.espresso : hover ? T.cream : T.white,
        color: active ? T.white : danger ? T.danger : T.espresso,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.15s',
        opacity: disabled ? 0.4 : 1,
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function BtnLabel({ children, color }: { children: string; color?: string }) {
  return (
    <span style={{ fontSize: 10, marginTop: 2, fontWeight: 500, color: color ?? 'inherit', lineHeight: 1 }}>
      {children}
    </span>
  );
}

function PrimaryBtn({ onClick, children, disabled }: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? T.creamMid : T.terra,
        color: disabled ? T.espressoLight : T.white,
        border: 'none', borderRadius: 999,
        padding: '16px 40px', fontSize: 16, fontWeight: 500,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit', letterSpacing: '0.01em',
        boxShadow: disabled ? 'none' : '0 1px 2px oklch(48% 0.14 45 / 0.2), 0 4px 20px oklch(48% 0.14 45 / 0.22)',
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.filter = 'brightness(0.94)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
    >
      {children}
    </button>
  );
}

function SecondaryBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: T.creamMid, color: T.espresso,
        border: 'none', borderRadius: 999,
        padding: '14px 28px', fontSize: 15, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'inherit',
        transition: 'all 0.15s',
        width: '100%',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.96)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
    >
      {children}
    </button>
  );
}

// ── Audio Check ───────────────────────────────────────────────────────────────

function inferNoiseReductionType(label: string): 'near_field' | 'far_field' {
  const l = label.toLowerCase();
  const nearField = ['headset', 'headphone', 'earphone', 'earpiece', 'airpod', 'earpod', 'earbud', 'bluetooth'];
  return nearField.some(k => l.includes(k)) ? 'near_field' : 'far_field';
}

const SELECT_STYLE: React.CSSProperties = {
  width: '100%', background: T.creamMid, color: T.espresso,
  border: `1px solid ${T.creamBorder}`, borderRadius: 8,
  padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
};

function AudioCheckScreen({ onStart }: {
  onStart: (stream: MediaStream, noiseReductionType: 'near_field' | 'far_field', outputDeviceId: string) => void;
}) {
  const VOICE_SAMPLE_SRC = '/audio/marin-audio-check.wav';
  const [micLevel, setMicLevel] = useState(0);
  const [micOk, setMicOk] = useState(false);
  const [speakerTested, setSpeakerTested] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [calibDone, setCalibDone] = useState(false);
  const [calibBarStarted, setCalibBarStarted] = useState(false);
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState('');

  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);
  const handedOffRef = useRef(false);
  const speakerTestAudioRef = useRef<HTMLAudioElement | null>(null);
  const calibDoneRef = useRef(false);
  const noiseSamplesRef = useRef<number[]>([]);
  const noiseFloorRef = useRef(0);
  const smoothedVoiceLevelRef = useRef(0);
  const calibTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const calibBarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setupStream = useCallback(async (deviceId?: string) => {
    cancelAnimationFrame(rafRef.current);
    if (calibTimerRef.current) clearTimeout(calibTimerRef.current);
    if (calibBarTimerRef.current) clearTimeout(calibBarTimerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    audioCtxRef.current?.close().catch(() => {});

    calibDoneRef.current = false;
    noiseSamplesRef.current = [];
    noiseFloorRef.current = 0;
    smoothedVoiceLevelRef.current = 0;
    setCalibDone(false);
    setCalibBarStarted(false);
    setMicLevel(0);
    setMicOk(false);
    setMicError(null);

    try {
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true, noiseSuppression: true, autoGainControl: true,
      };
      if (deviceId) audioConstraints.deviceId = { exact: deviceId };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      streamRef.current = stream;

      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        const avg = buf.reduce((s, v) => s + v, 0) / buf.length / 128;
        const rawLevel = Math.min(avg * 2.5, 1);
        if (!calibDoneRef.current) {
          noiseSamplesRef.current.push(rawLevel);
          setMicLevel(0);
        } else {
          // Only show voice energy above the calibrated background noise floor.
          const excess = Math.max(0, rawLevel - noiseFloorRef.current);
          // Conservative normalization to avoid single-syllable spikes hitting max.
          const normDenominator = Math.max(0.28, noiseFloorRef.current * 6 + 0.12);
          const normalized = Math.min(excess / normDenominator, 1);
          const voiceLevel = Math.pow(normalized, 0.9);
          const prev = smoothedVoiceLevelRef.current;
          const next = voiceLevel > prev
            ? (prev * 0.82 + voiceLevel * 0.18)
            : (prev * 0.88 + voiceLevel * 0.12);
          const clamped = next < 0.015 ? 0 : next;
          smoothedVoiceLevelRef.current = clamped;
          setMicLevel(clamped);
          if (clamped > 0.12) setMicOk(true);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      calibBarTimerRef.current = setTimeout(() => setCalibBarStarted(true), 50);
      calibTimerRef.current = setTimeout(() => {
        const samples = noiseSamplesRef.current;
        if (samples.length > 0) {
          const sorted = [...samples].sort((a, b) => a - b);
          const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
          noiseFloorRef.current = Math.min(Math.max(p95 + 0.015, 0.01), 0.45);
        } else {
          noiseFloorRef.current = 0.03;
        }
        calibDoneRef.current = true;
        setCalibDone(true);
      }, 2000);
    } catch (err) {
      setMicError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone access was denied. Please allow access and reload.'
          : 'Could not access microphone.',
      );
    }
  }, []);

  useEffect(() => {
    let active = true;
    setupStream().then(async () => {
      if (!active) return;
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (!active) return;
        const mics = all.filter(d => d.kind === 'audioinput' && d.deviceId);
        const speakers = all.filter(d => d.kind === 'audiooutput' && d.deviceId);
        setMicDevices(mics);
        setSpeakerDevices(speakers);
        const defMic = mics.find(d => d.deviceId === 'default') ?? mics[0];
        const defSpk = speakers.find(d => d.deviceId === 'default') ?? speakers[0];
        if (defMic) setSelectedMicId(defMic.deviceId);
        if (defSpk) setSelectedSpeakerId(defSpk.deviceId);
      } catch {}
    });
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      if (calibTimerRef.current) clearTimeout(calibTimerRef.current);
      if (calibBarTimerRef.current) clearTimeout(calibBarTimerRef.current);
      if (!handedOffRef.current) streamRef.current?.getTracks().forEach(t => t.stop());
      audioCtxRef.current?.close().catch(() => {});
      speakerTestAudioRef.current?.pause();
      speakerTestAudioRef.current = null;
    };
  }, [setupStream]);

  const handleMicChange = useCallback((deviceId: string) => {
    setSelectedMicId(deviceId);
    setupStream(deviceId);
  }, [setupStream]);

  const playTestSound = useCallback(async () => {
    speakerTestAudioRef.current?.pause();
    const audioEl = new Audio(VOICE_SAMPLE_SRC);
    audioEl.preload = 'auto';
    speakerTestAudioRef.current = audioEl;
    if (selectedSpeakerId && 'setSinkId' in audioEl) {
      try { await (audioEl as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(selectedSpeakerId); } catch {}
    }
    await audioEl.play().catch(() => {});
    setSpeakerTested(true);
  }, [selectedSpeakerId, VOICE_SAMPLE_SRC]);

  const noiseReductionType = inferNoiseReductionType(
    micDevices.find(d => d.deviceId === selectedMicId)?.label ?? '',
  );

  const handleStart = useCallback(() => {
    handedOffRef.current = true;
    cancelAnimationFrame(rafRef.current);
    if (calibTimerRef.current) clearTimeout(calibTimerRef.current);
    if (calibBarTimerRef.current) clearTimeout(calibBarTimerRef.current);
    audioCtxRef.current?.close().catch(() => {});
    onStart(streamRef.current!, noiseReductionType, selectedSpeakerId);
  }, [onStart, noiseReductionType, selectedSpeakerId]);

  const BARS = 10;
  const goodColor = T.sage;

  return (
    <div style={{
      minHeight: '100vh', background: T.cream,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px', gap: 28,
    }}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 className="serif" style={{ fontSize: 28, color: T.espresso, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          Audio check
        </h2>
        <p style={{ fontSize: 14, color: T.espressoLight }}>
          Confirm your mic and speakers before we begin.
        </p>
      </div>

      {/* Mic panel */}
      <div style={{
        width: '100%', maxWidth: 420, background: T.white,
        borderRadius: 16, border: `1px solid ${T.creamBorder}`,
        padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: T.espresso }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Microphone</span>
          </div>
          {micOk && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: goodColor, fontWeight: 500 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Detected
            </span>
          )}
        </div>

        {micDevices.length > 1 && (
          <select value={selectedMicId} onChange={e => handleMicChange(e.target.value)} style={SELECT_STYLE}>
            {micDevices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>
            ))}
          </select>
        )}

        {micError ? (
          <p style={{ fontSize: 13, color: T.danger, lineHeight: 1.5 }}>{micError}</p>
        ) : !calibDone ? (
          <>
            <p style={{ fontSize: 13, color: T.espressoLight }}>Measuring background noise…</p>
            <div style={{ height: 4, borderRadius: 2, background: T.creamMid, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2, background: T.terra,
                width: calibBarStarted ? '100%' : '0%',
                transition: 'width 2s linear',
              }} />
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: T.espressoLight }}>
              {micOk ? 'Your microphone is picking up sound.' : 'Say something to test your microphone…'}
            </p>
            <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 28 }}>
              {Array.from({ length: BARS }).map((_, i) => {
                const threshold = (i + 0.5) / BARS;
                const lit = micLevel > threshold;
                return (
                  <div key={i} style={{
                    flex: 1, borderRadius: 3,
                    height: `${40 + i * 6}%`,
                    background: lit ? (i < 7 ? T.terra : T.terraDeep) : T.creamMid,
                    transition: 'background 0.06s',
                  }} />
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Speaker panel */}
      <div style={{
        width: '100%', maxWidth: 420, background: T.white,
        borderRadius: 16, border: `1px solid ${T.creamBorder}`,
        padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: T.espresso }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 500 }}>Speakers</span>
          </div>
          {speakerTested && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: goodColor, fontWeight: 500 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Tested
            </span>
          )}
        </div>

        {speakerDevices.length > 1 && (
          <select value={selectedSpeakerId} onChange={e => setSelectedSpeakerId(e.target.value)} style={SELECT_STYLE}>
            {speakerDevices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>
            ))}
          </select>
        )}

        <p style={{ fontSize: 13, color: T.espressoLight }}>
          {speakerTested ? 'Did you hear the AI voice sample?' : 'Play the AI voice sample to confirm your audio output is on.'}
        </p>
        <button
          onClick={playTestSound}
          style={{
            alignSelf: 'flex-start',
            background: T.creamMid, color: T.espresso,
            border: 'none', borderRadius: 999,
            padding: '9px 18px', fontSize: 13, fontWeight: 500,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 6,
            transition: 'filter 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.95)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
          Play voice sample
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <PrimaryBtn onClick={handleStart}>
          Start Interview
        </PrimaryBtn>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function InterviewPage() {
  const session = useRealtimeSession();
  const { status, orbState, micLevel, aiLevel, ended, phase, error, currentAiText, aiTurnId } = session;

  const [audioCheck, setAudioCheck] = useState(false);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [messageFade, setMessageFade] = useState(1);
  const [displayText, setDisplayText] = useState('');

  // Fade transition when a new AI turn starts
  useEffect(() => {
    if (!aiTurnId) return;
    setMessageFade(0);
    const t = setTimeout(() => setMessageFade(1), 300);
    return () => clearTimeout(t);
  }, [aiTurnId]);

  // Update displayed text whenever the streaming text changes (no fade — just update)
  useEffect(() => {
    setDisplayText(currentAiText);
  }, [currentAiText]);

  // Elapsed timer — runs while connected and not paused/ended
  useEffect(() => {
    if (status !== 'ready' || paused || ended) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [status, paused, ended]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { session.stop(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    session.mute(next);
  }, [muted, session]);

  const handlePause = useCallback(() => {
    const next = !paused;
    setPaused(next);
    session.pause(next);
  }, [paused, session]);

  const handleEndConfirm = useCallback(() => {
    setShowEndConfirm(false);
    session.stop();
  }, [session]);

  const stateLabel =
    ended ? 'Interview complete'
    : paused ? '· · ·'
    : orbState === 'speaking' ? 'Interviewer speaking'
    : orbState === 'listening' ? "Your turn — I'm listening"
    : orbState === 'thinking' ? 'Thinking…'
    : '· · ·';

  // ── Audio check screen ────────────────────────────────────────────────────
  if (audioCheck) {
    return (
      <AudioCheckScreen onStart={(stream, noiseReductionType, outputDeviceId) => { setAudioCheck(false); session.start('marin', stream, noiseReductionType, outputDeviceId); }} />
    );
  }

  // ── Pre-start screen ──────────────────────────────────────────────────────
  if (status === 'idle') {
    return (
      <div style={{
        minHeight: '100vh', background: T.cream,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px', gap: 40, textAlign: 'center',
      }}>
        <Orb state="idle" size={200} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h1 className="serif" style={{ fontSize: 32, color: T.espresso, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            How people use AI for companionship
          </h1>
          <p style={{ fontSize: 15, color: T.espressoLight }}>
            Voice interview · ~20 minutes
          </p>
        </div>
        <PrimaryBtn onClick={() => setAudioCheck(true)}>
          Begin Interview
        </PrimaryBtn>
      </div>
    );
  }

  // ── Connecting screen ─────────────────────────────────────────────────────
  if (status === 'connecting') {
    return (
      <div style={{
        minHeight: '100vh', background: T.cream,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 32, padding: 24,
      }}>
        <Orb state="idle" size={200} />
        <p style={{ fontSize: 15, color: T.espressoLight }}>Requesting microphone…</p>
      </div>
    );
  }

  // ── Error screen ──────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div style={{
        minHeight: '100vh', background: T.cream,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 24, padding: '40px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto',
      }}>
        <Orb state="idle" size={160} />
        <p style={{ fontSize: 15, color: T.espressoMid, lineHeight: 1.6 }}>{error}</p>
        <PrimaryBtn onClick={() => { session.stop(); }}>
          Try again
        </PrimaryBtn>
      </div>
    );
  }

  // ── Ended screen ──────────────────────────────────────────────────────────
  if (ended && orbState === 'idle') {
    return (
      <div style={{
        minHeight: '100vh', background: T.cream,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 40, padding: '40px 24px', textAlign: 'center',
      }}>
        <Orb state="idle" size={200} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p className="serif" style={{ fontSize: 30, color: T.espresso, letterSpacing: '-0.015em' }}>
            Thank you for your time.
          </p>
          <p style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.espressoLight, fontWeight: 500 }}>
            Interview complete
          </p>
        </div>
      </div>
    );
  }

  // ── Active interview screen ───────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', background: T.cream,
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top bar */}
      {!ended && (
        <div style={{
          position: 'absolute', top: 24, left: 0, right: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 32px', zIndex: 5,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: T.terra, boxShadow: `0 0 0 3px oklch(62% 0.13 48 / 0.12)` }} />
            <span className="serif" style={{ fontSize: 16, color: T.espresso, letterSpacing: '-0.01em' }}>
              AI Interviewer
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.espressoLight, fontVariantNumeric: 'tabular-nums' }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: paused ? T.espressoLight : T.danger,
              animation: paused ? 'none' : 'pulse 1.6s ease-in-out infinite',
            }} />
            {paused ? 'Paused' : 'Recording'}
          </div>
        </div>
      )}

      {/* Center */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center',
      }} className="interview-stage">
        {/* Orb is the sole flex item; labels are absolutely positioned below so they
            never affect the orb's vertical position when text appears/disappears. */}
        <div style={{ position: 'relative', flexShrink: 0 }} className="interview-orb-wrap">
          <Orb
            state={paused ? 'idle' : orbState}
            size={240}
            micLevel={paused ? undefined : micLevel}
            aiLevel={orbState === 'speaking' ? aiLevel : undefined}
          />

          {/* State label */}
          <div style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)', whiteSpace: 'nowrap',
            fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase',
            color: T.espressoLight, fontWeight: 500,
            opacity: paused ? 0.4 : 1, transition: 'opacity 0.3s',
          }} className="interview-state-label">
            {stateLabel}
          </div>

          {/* AI message text */}
          <div style={{
            position: 'absolute', top: '100%', left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          }} className="interview-message-wrap">
            <p
              className="serif"
              style={{
                fontSize: 22, lineHeight: 1.5, color: T.espresso,
                letterSpacing: '-0.01em',
                opacity: messageFade, transition: 'opacity 0.3s ease',
                textWrap: 'pretty' as React.CSSProperties['textWrap'],
              }}
            >
              {paused
                ? 'Take your time.'
                : phase === 'interviewing'
                  ? displayText || ' '
                  : ended
                    ? 'Thank you for your time.'
                    : ' '}
            </p>
          </div>
        </div>
      </div>

      {/* Control bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: `linear-gradient(180deg, transparent 0%, ${T.cream} 60%)`,
        padding: '60px 24px 16px',
        display: 'flex', justifyContent: 'center',
        zIndex: 10,
      }}>
        <div style={{
          background: T.white, border: `1px solid ${T.creamBorder}`,
          borderRadius: 999, padding: '8px 12px',
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 24px oklch(18% 0.018 60 / 0.06)',
        }}>
          {/* Repeat */}
          <IconBtn onClick={session.repeat} label="Repeat question" disabled={ended || orbState === 'thinking'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            <BtnLabel>Repeat</BtnLabel>
          </IconBtn>

          {/* Mute mic */}
          <IconBtn onClick={handleMute} label={muted ? 'Unmute mic' : 'Mute mic'} active={muted} disabled={ended}>
            {muted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="2" y1="2" x2="22" y2="22" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            )}
            <BtnLabel>{muted ? 'Muted' : 'Mute'}</BtnLabel>
          </IconBtn>

          {/* Pause */}
          <IconBtn onClick={handlePause} label={paused ? 'Resume' : 'Pause'} active={paused} disabled={ended}>
            {paused ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            )}
            <BtnLabel>{paused ? 'Resume' : 'Pause'}</BtnLabel>
          </IconBtn>

          <div style={{ width: 1, height: 32, background: T.creamBorder, margin: '0 4px', flexShrink: 0 }} />

          {/* End */}
          <IconBtn onClick={() => setShowEndConfirm(true)} label="End interview" danger disabled={ended}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ended ? T.espressoLight : T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
            <BtnLabel color={ended ? T.espressoLight : T.danger}>End</BtnLabel>
          </IconBtn>
        </div>
      </div>

      {/* End confirm modal */}
      {showEndConfirm && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'oklch(18% 0.018 60 / 0.55)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24, zIndex: 100,
        }}>
          <div style={{
            background: T.white, borderRadius: 18, padding: 32,
            maxWidth: 440, width: '100%',
            display: 'flex', flexDirection: 'column', gap: 16,
            boxShadow: '0 20px 60px oklch(18% 0.018 60 / 0.25)',
          }}>
            <h3 className="serif" style={{ fontSize: 26, color: T.espresso, lineHeight: 1.25 }}>
              End the interview now?
            </h3>
            <p style={{ fontSize: 15, color: T.espressoMid, lineHeight: 1.6 }}>
              You've been talking for <strong>{fmtTime(elapsed)}</strong>. Your responses so far will be saved.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <SecondaryBtn onClick={() => setShowEndConfirm(false)}>
                Keep going
              </SecondaryBtn>
              <button
                onClick={handleEndConfirm}
                style={{
                  background: T.terra, color: T.white,
                  border: 'none', borderRadius: 999,
                  padding: '14px 28px', fontSize: 15, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit', width: '100%',
                  boxShadow: '0 1px 2px oklch(48% 0.14 45 / 0.2)',
                  transition: 'filter 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.94)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
              >
                Yes, end now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
